import type Highcharts from "highcharts";
import type {
  AssetRegisterAggregatedTimeSeriesResponse,
  AssetRegisterItemAggregatedTimeSeries,
} from "kubb";
import { round } from "lodash-es";
import type React from "react";
import { usePrivacyMode } from "../../reducers/privacy";
import {
  type AggregatedChartsAmendedOptions,
  type AggregatedDataItemClickHandlerProps,
  type AssetRegisterItemScatterColumn,
  type AssetRegisterItemScatterPoint,
  aggregationDataItemClickHandler,
} from "./AggregationChartFunctions";
import Chart from "./Chart";

declare global {
  interface Window {
    dayjs: any;
  }
}

export default function AggregationScatterChart(
  props: {
    data?: AssetRegisterAggregatedTimeSeriesResponse;
    xAggregation: string;
    xParameter: string;
    yAggregation: string;
    yParameter: string;
    loading: boolean;
    name?: string;
    height?: string;
    xUnit: string;
    yUnit: string;
    xLabel: string;
    yLabel: string;
  } & AggregatedDataItemClickHandlerProps,
): React.ReactElement {
  const { height = "400px" } = props;
  const privacyMode = usePrivacyMode();

  const xAggregation = props.xAggregation;
  const xParameter = props.xParameter;
  const yAggregation = props.yAggregation;
  const yParameter = props.yParameter;

  const dataMapper = (
    v: AssetRegisterItemAggregatedTimeSeries,
  ): AssetRegisterItemScatterColumn => {
    return {
      asset_position_id: v.asset_position_id,
      assetPositionReference: v.asset_position_reference,
      locationAddress: v.location_address,
      asset_id: v.asset_id,
      x: v.aggregations[xParameter]?.[xAggregation]?.[0] || undefined,
      y: v.aggregations[yParameter]?.[yAggregation]?.[0] || undefined,
    };
  };

  const seriesDefinition = (
    data: AssetRegisterAggregatedTimeSeriesResponse,
    name?: string,
    userRef?: string,
    color?: string,
  ): Highcharts.SeriesScatterOptions & AggregatedChartsAmendedOptions => {
    return {
      id: userRef,
      data: data.data.map(dataMapper),
      type: "scatter",
      color: color ? `var(--${color})` : undefined,
      animation: false,
      events: {
        click: aggregationDataItemClickHandler({
          start_time: props.start_time,
          end_time: props.end_time,
          privacyMode: privacyMode,
          company_id: props.company_id,
          scheme_id: props.scheme_id,
        }),
      },
      turboThreshold: 0,
      tooltip: {
        pointFormatter: function (this: AssetRegisterItemScatterPoint) {
          return (
            `<tspan style='fill:${this.color}'>●</tspan>` +
            `<tspan style='${privacyMode && "filter: url(#blur)"}'> 
${this.locationAddress} - ${this.assetPositionReference || "No Ref"}
             </tspan>` +
            `<tspan style='font-weight:bold'> ${round(this.y as number, 1)}${
              props.yUnit
            } ${round(this.x as number, 1)}${props.xUnit}</tspan>`
          );
        },
      },
    };
  };

  const series: Highcharts.SeriesScatterOptions[] = props.data
    ? [seriesDefinition(props.data)]
    : [];

  const options: Highcharts.Options = {
    title: {
      text: "",
    },
    chart: {
      type: "scatter",
      height: height,
    },
    xAxis: props.data
      ? [
          {
            labels: {
              format: `{value}${props.xUnit}`,
            },
            title: {
              text: props.xLabel,
            },
          },
        ]
      : [],
    // xAxis: {
    //   type: "datetime",
    //   min: props.params?.startDatetime?.valueOf(),
    //   max: props.params?.endDatetime?.valueOf(),
    // },
    series: series,
    yAxis: props.data
      ? [
          {
            labels: {
              format: `{value}${props.yUnit}`,
            },
            title: {
              text: props.yLabel,
            },
          },
        ]
      : [],
    legend: {
      enabled: false,
    },
    tooltip: {
      shared: true,
      // valueSuffix: props.unit,
      // valueDecimals: props.rounding,
      // pointFormat: `{series.name} {point.y}${props.unit}`
    },
  };

  return (
    <Chart
      options={options}
      loading={props.loading}
      privacyMode={privacyMode}
    />
  );
}
